/**
 * useSocialConnections — TanStack Query composable
 *
 * Single source of truth for social channel connection data.
 * Replaces the Vuex `social` module (src/store/modules/social.js).
 *
 * The ['social-connections'] query is pre-populated at startup in
 * src/stores/auth.ts (checkToken). This composable simply
 * reads from that same cache — no extra network request is made.
 */
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import apiInterceptor from '@/shared/apiInterceptor'
import { environment } from '@/shared/env'
import type { SocialConnections, WhatsAppConnection, GoogleRCSConnection, SMSConnection } from '@/shared/types'

const EMPTY_CONNECTIONS: SocialConnections = {
  whatsapp: [],
  googlercs: [],
  sms: [],
  email: [],
}

export const socialConnectionsQueryOptions = {
  queryKey: ['social-connections'] as const,
  queryFn: async (): Promise<SocialConnections> => {
    // apiInterceptor unwraps response.data at the interceptor level; cast is required
    // because apiInterceptor.js has no TypeScript types (inferred as AxiosResponse).
    const r = await apiInterceptor.get(environment('VUE_APP_API_URL') + 'social/v1/connections') as SocialConnections | null
    return r ?? EMPTY_CONNECTIONS
  },
  staleTime: Infinity,
}

export function useSocialConnections() {
  const { data, isLoading, isError, error } = useQuery(socialConnectionsQueryOptions)

  const whatsapp = computed<WhatsAppConnection[]>(() => data.value?.whatsapp ?? [])
  const googlercs = computed<GoogleRCSConnection[]>(() => data.value?.googlercs ?? [])
  const sms = computed<SMSConnection[]>(() => data.value?.sms ?? [])
  const email = computed<any[]>(() => data.value?.email ?? [])

  const isWhatsAppConnected = computed<boolean>(() => whatsapp.value.length > 0)
  const isGooglercsConnected = computed<boolean>(() => googlercs.value.length > 0)
  const isSMSConnected = computed<boolean>(() => sms.value.length > 0)
  const isEmailConnected = computed<boolean>(() => email.value.length > 0)

  const getWhatsApp = computed<WhatsAppConnection | null>(() => whatsapp.value[0] ?? null)
  const getGooglercs = computed<GoogleRCSConnection | null>(() => googlercs.value[0] ?? null)
  const getSMS = computed<SMSConnection | null>(() => sms.value[0] ?? null)
  const getEmail = computed<any | null>(() => email.value[0] ?? null)

  const connectedChannels = computed<(keyof SocialConnections)[]>(() => {
    const channels: (keyof SocialConnections)[] = []
    if (isWhatsAppConnected.value) channels.push('whatsapp')
    if (isGooglercsConnected.value) channels.push('googlercs')
    if (isSMSConnected.value) channels.push('sms')
    if (isEmailConnected.value) channels.push('email')
    return channels
  })

  return {
    // Raw data
    whatsapp,
    googlercs,
    sms,
    email,
    // Connection flags (mirrors former Vuex getters)
    isWhatsAppConnected,
    isGooglercsConnected,
    isSMSConnected,
    isEmailConnected,
    // First-entry shortcuts (mirrors former Vuex getters)
    getWhatsApp,
    getGooglercs,
    getSMS,
    getEmail,
    // Derived
    connectedChannels,
    // Query meta
    isLoading,
    isError,
    error,
  }
}
